<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { components } from '@/helpers/api/types/openapi/components.openapi';

export interface Props {
  message?: ReadonlyDeep<components['schemas']['SMSHistoryMessage']>;
}

const props = withDefaults(defineProps<Props>(), {
  message: undefined,
});

function getStopDisplay(stop: {
  address_line_1?: string;
  suburb?: string;
  post_code?: string;
}): string {
  const parts = [];
  if (stop.address_line_1) parts.push(stop.address_line_1);
  if (stop.suburb) parts.push(stop.suburb);
  if (stop.post_code) parts.push(stop.post_code);
  return parts.join(', ') || 'No address';
}
</script>

<template>
  <div :class="$style.smsHistoryTableFold" data-testid="smsHistoryTableFold">
    <div :class="$style.grid">
      <div :class="$style.field">
        <span :class="$style.label">Job ID</span>
        <span :class="$style.value">{{ props.message?.job_id || '-' }}</span>
      </div>

      <div
        v-if="props.message?.stops && props.message.stops.length > 0"
        :class="$style.field"
      >
        <span :class="$style.label">Stop 1</span>
        <span :class="$style.value">{{
          getStopDisplay(props.message.stops[0])
        }}</span>
      </div>

      <div :class="$style.field">
        <span :class="$style.label">Extra number</span>
        <span :class="$style.value">{{
          props.message?.extra_number || 'Not available'
        }}</span>
      </div>

      <div :class="$style.field">
        <span :class="$style.label">Offsider</span>
        <span :class="$style.value">{{
          props.message?.offsiders_list || '-'
        }}</span>
      </div>

      <div
        v-if="props.message?.stops && props.message.stops.length > 1"
        :class="$style.field"
      >
        <span :class="$style.label">Stop 2</span>
        <span :class="$style.value">{{
          getStopDisplay(props.message.stops[1])
        }}</span>
      </div>

      <div :class="$style.field">
        <span :class="$style.label">Message</span>
        <div :class="$style.messageValue">{{
          props.message?.content || 'No message content'
        }}</div>
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
@use '@/styles/colors';

.smsHistoryTableFold {
  padding: 24px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 24px;
  font-size: 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label {
  color: colors.$color-text-secondary;
  font-size: 14px;
}

.value {
  color: colors.$color-text-primary;
  font-size: 14px;
  font-weight: 600;
}

.messageValue {
  color: colors.$color-text-primary;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}
</style>
